import React, { useEffect, useState, Children } from 'react';
import { TrendingDown } from 'lucide-react';
interface ExpensesProps {
  liabilityPayments?: number;
  onExpensesChange?: (totalExpenses: number) => void;
}
export function Expenses({
  liabilityPayments = 0,
  onExpensesChange
}: ExpensesProps) {
  const [expenseValues, setExpenseValues] = useState({
    taxes: 0,
    homeMortgage: 0,
    schoolLoan: 0,
    carLoan: 0,
    creditCard: 0,
    retail: 0,
    other: 0,
    childExpenses: 0,
    loan: 0
  });
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [perChildExpense, setPerChildExpense] = useState(0);
  // Update loan expense when liabilityPayments changes
  useEffect(() => {
    setExpenseValues(prev => ({
      ...prev,
      loan: liabilityPayments
    }));
  }, [liabilityPayments]);
  const handleExpenseChange = (category: keyof typeof expenseValues, valueStr: string) => {
    // Convert input to number, defaulting to 0 if invalid
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    setExpenseValues({
      ...expenseValues,
      [category]: numValue
    });
  };
  const handleChildrenCountChange = (valueStr: string) => {
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    setNumberOfChildren(numValue);
  };
  const handlePerChildExpenseChange = (valueStr: string) => {
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    setPerChildExpense(numValue);
  };
  const totalChildExpenses = numberOfChildren * perChildExpense;
  // Update child expenses whenever number of children or per child expense changes
  useEffect(() => {
    setExpenseValues(prev => ({
      ...prev,
      childExpenses: totalChildExpenses
    }));
  }, [numberOfChildren, perChildExpense, totalChildExpenses]);
  const totalMonthlyExpenses = Object.values(expenseValues).reduce((sum, expense) => sum + expense, 0);
  // Call the callback whenever expenses change
  useEffect(() => {
    if (onExpensesChange) {
      onExpensesChange(totalMonthlyExpenses);
    }
  }, [totalMonthlyExpenses, onExpensesChange]);
  return <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-46">
      <div className="flex items-center justify-between mb-4" data-id="element-47">
        <div className="flex items-center" data-id="element-48">
          <TrendingDown size={20} className="text-red-500 mr-2" data-id="element-49" />
          <h2 className="text-xl font-semibold" data-id="element-50">Expenses</h2>
        </div>
        <p className="text-lg font-bold text-red-600" data-id="element-51">
          ${totalMonthlyExpenses.toLocaleString()}/mo
        </p>
      </div>
      <div className="space-y-3" data-id="element-52">
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-53">
          <p className="text-gray-700 font-medium" data-id="element-54">Taxes:</p>
          <div className="flex items-center" data-id="element-55">
            <span className="mr-1" data-id="element-56">$</span>
            <input type="number" value={expenseValues.taxes === 0 ? '' : expenseValues.taxes} onChange={e => handleExpenseChange('taxes', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-57" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-58">
          <p className="text-gray-700 font-medium" data-id="element-59">Home Mortgage Payment:</p>
          <div className="flex items-center" data-id="element-60">
            <span className="mr-1" data-id="element-61">$</span>
            <input type="number" value={expenseValues.homeMortgage === 0 ? '' : expenseValues.homeMortgage} onChange={e => handleExpenseChange('homeMortgage', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-62" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-63">
          <p className="text-gray-700 font-medium" data-id="element-64">School Loan Payment:</p>
          <div className="flex items-center" data-id="element-65">
            <span className="mr-1" data-id="element-66">$</span>
            <input type="number" value={expenseValues.schoolLoan === 0 ? '' : expenseValues.schoolLoan} onChange={e => handleExpenseChange('schoolLoan', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-67" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-68">
          <p className="text-gray-700 font-medium" data-id="element-69">Car Loan Payment:</p>
          <div className="flex items-center" data-id="element-70">
            <span className="mr-1" data-id="element-71">$</span>
            <input type="number" value={expenseValues.carLoan === 0 ? '' : expenseValues.carLoan} onChange={e => handleExpenseChange('carLoan', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-72" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-73">
          <p className="text-gray-700 font-medium" data-id="element-74">Credit Card Payment:</p>
          <div className="flex items-center" data-id="element-75">
            <span className="mr-1" data-id="element-76">$</span>
            <input type="number" value={expenseValues.creditCard === 0 ? '' : expenseValues.creditCard} onChange={e => handleExpenseChange('creditCard', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-77" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-78">
          <p className="text-gray-700 font-medium" data-id="element-79">Retail Payment:</p>
          <div className="flex items-center" data-id="element-80">
            <span className="mr-1" data-id="element-81">$</span>
            <input type="number" value={expenseValues.retail === 0 ? '' : expenseValues.retail} onChange={e => handleExpenseChange('retail', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-82" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-83">
          <p className="text-gray-700 font-medium" data-id="element-84">Other Expenses:</p>
          <div className="flex items-center" data-id="element-85">
            <span className="mr-1" data-id="element-86">$</span>
            <input type="number" value={expenseValues.other === 0 ? '' : expenseValues.other} onChange={e => handleExpenseChange('other', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" data-id="element-87" />
          </div>
        </div>
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-88">
          <p className="text-gray-700 font-medium" data-id="element-89">Liabilities Payment:</p>
          <div className="flex items-center" data-id="element-90">
            <span className="mr-1" data-id="element-91">$</span>
            <input type="number" value={expenseValues.loan === 0 ? '' : expenseValues.loan} onChange={e => handleExpenseChange('loan', e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-red-500 focus:outline-none w-24" disabled={liabilityPayments > 0} data-id="element-92" />
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-gray-100 rounded-lg" data-id="element-93">
        <h3 className="font-semibold mb-3" data-id="element-94">Children Expenses</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-95">
          <div data-id="element-96">
            <p className="text-gray-700 mb-1" data-id="element-97">Number of Children:</p>
            <div className="flex items-center" data-id="element-98">
              <input type="number" value={numberOfChildren === 0 ? '' : numberOfChildren} onChange={e => handleChildrenCountChange(e.target.value)} placeholder="0" className="font-medium bg-transparent border-b border-gray-300 focus:border-red-500 focus:outline-none w-full" data-id="element-99" />
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-100">
              (Begin game with 0 Children)
            </p>
          </div>
          <div data-id="element-101">
            <p className="text-gray-700 mb-1" data-id="element-102">Per Child Expense:</p>
            <div className="flex items-center" data-id="element-103">
              <span className="mr-1" data-id="element-104">$</span>
              <input type="number" value={perChildExpense === 0 ? '' : perChildExpense} onChange={e => handlePerChildExpenseChange(e.target.value)} placeholder="0" className="font-medium bg-transparent border-b border-gray-300 focus:border-red-500 focus:outline-none w-full" data-id="element-105" />
            </div>
          </div>
        </div>
        <div className="mt-3" data-id="element-106">
          <p className="text-gray-700 font-medium" data-id="element-107">Child Expenses:</p>
          <p className="font-medium text-red-600" data-id="element-108">
            ${totalChildExpenses.toLocaleString()}
          </p>
        </div>
      </div>
    </div>;
}